import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap';
import { calculateStopLoss } from '../utils/riskCalculations';

const StopLossCalculator = () => {
  // Form state
  const [entryPrice, setEntryPrice] = useState(100);
  const [isLong, setIsLong] = useState(true);
  const [method, setMethod] = useState('percentage');
  const [atrValue, setAtrValue] = useState(1);
  const [atrMultiplier, setAtrMultiplier] = useState(2);
  const [percentageValue, setPercentageValue] = useState(5);
  const [technicalLevel, setTechnicalLevel] = useState(95);
  
  // Toggle for multiple methods
  const [showAll, setShowAll] = useState(false);
  
  // Results state
  const [result, setResult] = useState(null);
  const [percentageResult, setPercentageResult] = useState(null);
  const [atrResult, setAtrResult] = useState(null);
  const [technicalResult, setTechnicalResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Calculate recommended stop loss levels
  const calculateStops = () => {
    try {
      setError(null);
      
      // Validate the entry price
      if (!entryPrice || entryPrice <= 0) {
        throw new Error("Entry price must be positive");
      }
      
      let calculatedResult;
      
      // Calculate stop loss based on selected method
      switch (method) {
        case 'atr':
          if (!atrValue || atrValue <= 0) {
            throw new Error("ATR value must be positive");
          }
          calculatedResult = calculateStopLoss(entryPrice, isLong, atrValue, atrMultiplier);
          break;
          
        case 'percentage':
          if (!percentageValue || percentageValue <= 0) {
            throw new Error("Percentage value must be positive");
          }
          calculatedResult = calculateStopLoss(entryPrice, isLong, null, 0, percentageValue);
          break;
          
        case 'technical':
          if (!technicalLevel || technicalLevel <= 0) {
            throw new Error("Technical level must be positive");
          }
          
          // For technical levels, validate stop loss is on the correct side of entry
          if (isLong && technicalLevel >= entryPrice) {
            throw new Error("For long positions, stop loss must be below entry price");
          } else if (!isLong && technicalLevel <= entryPrice) {
            throw new Error("For short positions, stop loss must be above entry price");
          }
          
          // Calculate distance/percentage for technical level
          const distance = Math.abs(entryPrice - technicalLevel);
          const distancePercentage = (distance / entryPrice) * 100;
          
          calculatedResult = {
            stopLossPrice: technicalLevel,
            method: 'Technical/Support Level',
            distance,
            distancePercentage,
            distanceFormatted: distancePercentage.toFixed(2) + '%',
          };
          break;
          
        default:
          throw new Error("Invalid stop loss method");
      }
      
      setResult(calculatedResult);
      
      // If showing all methods, calculate all of them
      if (showAll) {
        // Percentage-based
        setPercentageResult(calculateStopLoss(entryPrice, isLong, null, 0, percentageValue));
        
        // ATR-based
        if (atrValue > 0) {
          setAtrResult(calculateStopLoss(entryPrice, isLong, atrValue, atrMultiplier));
        }
        
        // Technical level
        if (technicalLevel > 0) {
          const distance = Math.abs(entryPrice - technicalLevel);
          const distancePercentage = (distance / entryPrice) * 100;
          
          setTechnicalResult({
            stopLossPrice: technicalLevel,
            method: 'Technical/Support Level',
            distance,
            distancePercentage,
            distanceFormatted: distancePercentage.toFixed(2) + '%',
          });
        }
      }
    } catch (err) {
      setError(err.message);
      setResult(null);
    }
  };
  
  // Update technical level when direction changes
  useEffect(() => {
    const defaultStopDistance = entryPrice * 0.05; // 5% default
    if (isLong) {
      setTechnicalLevel(entryPrice - defaultStopDistance);
    } else {
      setTechnicalLevel(entryPrice + defaultStopDistance);
    }
  }, [isLong, entryPrice]);
  
  // Reset form to defaults
  const handleReset = () => {
    setEntryPrice(100);
    setIsLong(true);
    setMethod('percentage');
    setAtrValue(1);
    setAtrMultiplier(2);
    setPercentageValue(5);
    setTechnicalLevel(95);
    setShowAll(false);
    setResult(null);
    setPercentageResult(null);
    setAtrResult(null);
    setTechnicalResult(null);
    setError(null);
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="card-header-gradient">
        <h4 className="mb-0">
          <i className="bi bi-shield-x me-2"></i>
          Stop Loss Calculator
        </h4>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entry Price</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0.0001"
                    step="0.01"
                    value={entryPrice}
                    onChange={(e) => setEntryPrice(parseFloat(e.target.value))}
                  />
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Trade Direction</Form.Label>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    id="direction-long"
                    label="Long"
                    checked={isLong}
                    onChange={() => setIsLong(true)}
                    className="me-3"
                  />
                  <Form.Check
                    type="radio"
                    id="direction-short"
                    label="Short"
                    checked={!isLong}
                    onChange={() => setIsLong(false)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Stop Loss Method</Form.Label>
                <div className="d-flex flex-wrap">
                  <Form.Check
                    type="radio"
                    id="method-percentage"
                    label="Percentage"
                    checked={method === 'percentage'}
                    onChange={() => setMethod('percentage')}
                    className="me-3 mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="method-atr"
                    label="ATR (Volatility)"
                    checked={method === 'atr'}
                    onChange={() => setMethod('atr')}
                    className="me-3 mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="method-technical"
                    label="Technical Level"
                    checked={method === 'technical'}
                    onChange={() => setMethod('technical')}
                    className="me-3 mb-2"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          {method === 'percentage' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Percentage</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={percentageValue}
                      onChange={(e) => setPercentageValue(parseFloat(e.target.value))}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <Form.Text className="text-muted">
                    Stop loss will be placed {percentageValue}% {isLong ? 'below' : 'above'} entry price
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          
          {method === 'atr' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ATR Value</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.0001"
                    step="0.01"
                    value={atrValue}
                    onChange={(e) => setAtrValue(parseFloat(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Enter the Average True Range value for this instrument
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ATR Multiplier</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={atrMultiplier}
                    onChange={(e) => setAtrMultiplier(parseFloat(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Common values: 2 (normal volatility), 3 (higher volatility)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          
          {method === 'technical' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Technical/Support Level</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      min="0.0001"
                      step="0.00001"
                      value={technicalLevel}
                      onChange={(e) => setTechnicalLevel(parseFloat(e.target.value))}
                    />
                  </div>
                  <Form.Text className={isLong ? 'text-danger' : 'text-success'}>
                    For {isLong ? 'long' : 'short'} positions, this must be {isLong ? 'below' : 'above'} entry price
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="show-all-methods"
              label="Show Comparison of All Methods"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
          </Form.Group>
          
          <div className="d-flex mt-4">
            <Button variant="primary" onClick={calculateStops} className="me-2">
              <i className="bi bi-calculator me-2"></i>
              Calculate
            </Button>
            <Button variant="outline-secondary" onClick={handleReset}>
              <i className="bi bi-arrow-repeat me-1"></i>
              Reset
            </Button>
          </div>
        </Form>
        
        {result && (
          <div className="mt-4">
            <h5 className="mb-3">
              <span className="gradient-text">Stop Loss Results</span>
            </h5>
            
            <Table responsive className="table">
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Stop Loss Price</th>
                  <th>Distance</th>
                  <th>Distance %</th>
                </tr>
              </thead>
              <tbody>
                <tr className={method === 'percentage' ? 'table-active' : ''}>
                  <td>
                    {showAll ? 'Percentage-based' : result.method}
                    {method === 'percentage' && !showAll && (
                      <i className="bi bi-check-circle-fill text-success ms-2"></i>
                    )}
                  </td>
                  <td>
                    <strong>${showAll ? percentageResult?.stopLossPrice.toFixed(5) : result.stopLossPrice.toFixed(5)}</strong>
                  </td>
                  <td>
                    ${showAll ? percentageResult?.distance.toFixed(5) : result.distance.toFixed(5)}
                  </td>
                  <td>
                    {showAll ? percentageResult?.distancePercentage.toFixed(5) : result.distancePercentage.toFixed(5)}%
                  </td>
                </tr>
                
                {(showAll || method === 'atr') && atrValue > 0 && (
                  <tr className={method === 'atr' ? 'table-active' : ''}>
                    <td>
                      ATR-based ({atrMultiplier}x ATR)
                      {method === 'atr' && !showAll && (
                        <i className="bi bi-check-circle-fill text-success ms-2"></i>
                      )}
                    </td>
                    <td>
                      <strong>${showAll ? atrResult?.stopLossPrice.toFixed(2) : (method === 'atr' ? result.stopLossPrice.toFixed(2) : '-')}</strong>
                    </td>
                    <td>
                      ${showAll ? atrResult?.distance.toFixed(5) : (method === 'atr' ? result.distance.toFixed(5) : '-')}
                    </td>
                    <td>
                      {showAll ? atrResult?.distancePercentage.toFixed(2) : (method === 'atr' ? result.distancePercentage.toFixed(2) : '-')}%
                    </td>
                  </tr>
                )}
                
                {(showAll || method === 'technical') && (
                  <tr className={method === 'technical' ? 'table-active' : ''}>
                    <td>
                      Technical Level
                      {method === 'technical' && !showAll && (
                        <i className="bi bi-check-circle-fill text-success ms-2"></i>
                      )}
                    </td>
                    <td>
                      <strong>${showAll ? technicalResult?.stopLossPrice.toFixed(5) : (method === 'technical' ? result.stopLossPrice.toFixed(5) : '-')}</strong>
                    </td>
                    <td>
                      ${showAll ? technicalResult?.distance.toFixed(5) : (method === 'technical' ? result.distance.toFixed(5) : '-')}
                    </td>
                    <td>
                      {showAll ? technicalResult?.distancePercentage.toFixed(5) : (method === 'technical' ? result.distancePercentage.toFixed(5) : '-')}%
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            
            <Alert variant="info" className="mt-3">
              <h6 className="alert-heading mb-2">
                <i className="bi bi-info-circle me-2"></i>
                Stop Loss Placement Guide
              </h6>
              <ul className="mb-0">
                <li>
                  <strong>Percentage-based:</strong> Simple method that ignores market conditions. Best for quick calculations but may not respect market structure.
                </li>
                <li>
                  <strong>ATR-based:</strong> Adapts to market volatility. Widens in volatile markets and tightens in calm markets. Generally recommended for most traders.
                </li>
                <li>
                  <strong>Technical Level:</strong> Places stops at key support/resistance levels, respecting market structure. Most effective but requires proper technical analysis.
                </li>
              </ul>
            </Alert>
            
            {result.distancePercentage > 10 && (
              <Alert variant="warning" className="mt-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                This stop loss is {result.distancePercentage.toFixed(2)}% from your entry price, which may be quite wide. Consider reducing position size to manage risk.
              </Alert>
            )}
            
            {result.distancePercentage < 1 && (
              <Alert variant="warning" className="mt-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                This stop loss is very tight at only {result.distancePercentage.toFixed(2)}% from your entry price. It may be hit by normal market noise before your trade has a chance to work.
              </Alert>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default StopLossCalculator;